import React, { useEffect, useRef } from "react";
import cx from "classnames";

import UIActions from "../actions/UIActions";
import { toScreenSpace, toZoomedSpace } from "@/utils/ZoomUtils";

import styles from "./Ruler.module.css";

/**
 * Ruler component for rendering a ruler with resize functionality.
 *
 * @param {Object} props - The properties object.
 * @param {number=} [props.numericMarkerAt=100] - Interval for numeric markers on the ruler.
 * @param {boolean=} [props.resizeEnabled=true] - Flag to enable or disable resizing.
 * @param {number=} [props.selectionStart=0] - Starting position of the selection.
 * @param {number=} [props.selectionSize=0] - Size of the selection.
 * @param {"vertical" | "horizontal"} props.direction - Direction of the ruler, either 'horizontal' or 'vertical'.
 * @param {number=} props.height - Height of the ruler.
 * @param {number=} props.width - Width of the ruler.
 * @param {function=} props.onResizeStart - Callback function triggered when resizing starts.
 * @param {function=} props.onResize - Callback function triggered during resizing.
 * @param {function=} props.onResizeComplete - Callback function triggered when resizing completes.
 * @param {React.RefObject} props.domRef - Reference to the DOM element of the ruler.
 * @param {number=} props.zoom - Zoom level for the ruler.
 * @returns {JSX.Element} The rendered Ruler component.
 */
const Ruler = ({
  numericMarkerAt = 100,
  resizeEnabled = true,
  selectionStart = 0,
  selectionSize = 0,
  direction,
  height,
  width,
  onResizeStart,
  onResize,
  onResizeComplete,
  domRef,
  zoom = 1,
}) => {
  /** The running resize drag, owned by the pointer that pressed. @type {React.MutableRefObject<{ pointerId: number, startY: number, startHeight: number, height: number, listeners: AbortController } | null>} */
  const drag = useRef(null);

  // a drag outliving the ruler mustn't keep listening
  useEffect(() => () => drag.current?.listeners.abort(), []);

  const getRulerUnits = (size) => {
    const rulerUnits = [];
    for (let i = 0; i <= size; ) {
      rulerUnits.push(
        <div key={i} className={styles.rulerUnit}>
          {i}
        </div>,
      );
      i += numericMarkerAt;
    }
    return rulerUnits;
  };

  // a finger moving or lifting while the Pencil resizes isn't the resize's
  const ownsDrag = (event) => event.pointerId === drag.current?.pointerId;

  const handleDragMove = (event) => {
    if (!ownsDrag(event)) return;
    const { startY, startHeight } = drag.current;
    const scaledDiff = Math.round(toZoomedSpace(event.clientY - startY, zoom));
    drag.current.height = Math.max(1, startHeight + scaledDiff);
    onResize?.(drag.current.height);
  };

  /** Commits the running drag's height and stops listening for it. */
  const endDrag = () => {
    const { height, listeners } = drag.current;
    drag.current = null;
    listeners.abort();
    onResizeComplete?.(height);
  };

  const handleDragEnd = (event) => {
    if (ownsDrag(event)) endDrag();
  };

  const handleMouseDown = (e) => {
    if (!resizeEnabled) return;
    // a second press ends the first drag, whose listeners would otherwise
    // stay attached alongside the new ones
    if (drag.current) endDrag();

    // the press attaches its own listeners, not an effect after the render:
    // StageGestures replays a tap's or long-press's press immediately before
    // its release or cancel, which must find them
    const listeners = new AbortController();
    drag.current = {
      pointerId: e.pointerId,
      startY: e?.clientY ?? 0,
      startHeight: selectionSize,
      height: selectionSize,
      listeners,
    };
    const options = { signal: listeners.signal };
    window.addEventListener("pointermove", handleDragMove, options);
    window.addEventListener("pointerup", handleDragEnd, options);
    // an interrupted touch keeps the height it reached
    window.addEventListener("pointercancel", handleDragEnd, options);

    // Deselect elements to prevent accidental movement during canvas resizing
    UIActions.clearSelectedElements();
    onResizeStart?.(selectionSize);
  };

  const style = {
    "--ruler-unit-size": `${toScreenSpace(numericMarkerAt, zoom)}px`,
    ...(direction === "horizontal" && {
      "--main-dim-width": `${toScreenSpace(width, zoom)}px`,
    }),
  };

  let mainDimensionsStyle = {};
  let rulerUnits = [];
  let rulerSliderStyle = {};
  let postDimensionsStyle = {};
  let preDimensionsStyle = {};
  let blockResizer = null;

  if (direction === "vertical") {
    const postDimensionsStart =
      numericMarkerAt - (selectionSize % numericMarkerAt);

    preDimensionsStyle = {
      height: `${toScreenSpace(selectionStart, zoom)}px`,
    };

    mainDimensionsStyle = {
      height: `${toScreenSpace(selectionSize, zoom)}px`,
    };

    rulerUnits = getRulerUnits(height);

    postDimensionsStyle = {
      backgroundPosition: `0 ${toScreenSpace(postDimensionsStart, zoom)}px`,
    };

    rulerSliderStyle = {
      height: `${toScreenSpace(height, zoom)}px`,
    };

    if (selectionSize > 0 && resizeEnabled) {
      blockResizer = (
        <div
          key="resizeHandle"
          className={styles.resizeHandle}
          onPointerDown={handleMouseDown}
        >
          <div className={styles.resizeIcon} />
        </div>
      );
    }
  } else {
    mainDimensionsStyle = {
      width: `${toScreenSpace(width, zoom)}px`,
    };

    rulerUnits = getRulerUnits(width);
    const postDimensionsStart = numericMarkerAt - (width % numericMarkerAt);
    postDimensionsStyle = {
      backgroundPosition: `${toScreenSpace(postDimensionsStart, zoom)}px 0`,
    };
  }

  const rulerCx = cx(styles.ruler, {
    "ruler-vertical": direction === "vertical",
    [styles.vertical]: direction === "vertical",
    "ruler-horizontal": direction === "horizontal",
    [styles.horizontal]: direction === "horizontal",
  });

  return (
    <div style={style} className={rulerCx} ref={domRef}>
      <div className={styles.sliderContainer} style={rulerSliderStyle}>
        <div className={styles.preDimensions} style={preDimensionsStyle} />
        <div className={styles.mainDimensions} style={mainDimensionsStyle}>
          {rulerUnits}
        </div>
        {blockResizer}
        <div className={styles.postDimensions} style={postDimensionsStyle} />
      </div>
    </div>
  );
};

export default Ruler;
